import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Loader2, TrendingUp, AlertTriangle, Award, FileDown } from "lucide-react";
import { toast } from "sonner";

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

export default function OwnerHeroStrip() {
  const [data, setData] = useState({ captured: null, atRisk: null, grade: null, mgrCount: null });
  const [loading, setLoading] = useState(true);
  const [dlBusy, setDlBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [roi, leaks, board] = await Promise.allSettled([
      apiClient.get("/roi-proof"),
      apiClient.get("/revenue-leaks"),
      apiClient.get("/board-report?period=month"),
    ]);
    setData({
      captured: roi.status === "fulfilled" ? roi.value.data.valueCapturedGBP : null,
      atRisk: leaks.status === "fulfilled" ? (leaks.value.data.totalImpactGBP ?? 0) : null,
      leakCount: leaks.status === "fulfilled" ? (leaks.value.data.count ?? 0) : null,
      grade: board.status === "fulfilled" ? board.value.data.avgManagerGrade : null,
      mgrCount: board.status === "fulfilled" ? board.value.data.managerCount : null,
    });
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportBoardPack = useCallback(async () => {
    setDlBusy(true);
    try {
      const res = await apiClient.get("/board-pack/export?period=month", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `board-pack-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Board pack downloaded — open in browser and print as PDF.");
    } catch (e) {
      toast.error("Failed to generate board pack.");
    } finally { setDlBusy(false); }
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="owner-hero-strip">
      {/* Revenue Captured */}
      <Card className="p-4 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="hero-revenue-captured">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-emerald-700"/>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800">Revenue Captured</div>
        </div>
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700"/> : (
          <>
            <div className="font-heading font-black text-3xl text-emerald-900" data-testid="hero-captured-value">{fmtGBP(data.captured)}</div>
            <div className="text-[11px] text-emerald-700 mt-1">via actioned intelligence</div>
          </>
        )}
      </Card>

      {/* Revenue At Risk */}
      <Card className="p-4 border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid="hero-revenue-at-risk">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-lg bg-rose-100 flex items-center justify-center">
            <AlertTriangle className="h-4 w-4 text-rose-700"/>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-800">Revenue At Risk</div>
        </div>
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-rose-700"/> : (
          <>
            <div className="font-heading font-black text-3xl text-rose-900" data-testid="hero-risk-value">{fmtGBP(data.atRisk)}</div>
            <div className="text-[11px] text-rose-700 mt-1">{data.leakCount || 0} open leak{data.leakCount === 1 ? "" : "s"}</div>
          </>
        )}
      </Card>

      {/* Team Grade */}
      <Card className="p-4 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="hero-team-grade">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Award className="h-4 w-4 text-indigo-700"/>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-800">Team Grade</div>
        </div>
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-indigo-700"/> : (
          <>
            <div className="font-heading font-black text-3xl text-indigo-900" data-testid="hero-grade-value">{data.grade || "—"}</div>
            <div className="text-[11px] text-indigo-700 mt-1">avg across {data.mgrCount || 0} manager{data.mgrCount === 1 ? "" : "s"}</div>
          </>
        )}
      </Card>

      {/* Export Board Pack — bold CTA */}
      <Card className="p-4 border-indigo-600 bg-indigo-600 text-white shadow-md hover:shadow-lg transition-shadow" data-testid="hero-board-pack">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-lg bg-indigo-500 flex items-center justify-center">
            <FileDown className="h-4 w-4 text-white"/>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-100">Board Pack</div>
        </div>
        <div className="font-heading font-bold text-lg leading-tight mb-3">Trustee-ready<br/>in one click</div>
        <Button
          size="sm"
          onClick={exportBoardPack}
          disabled={dlBusy}
          className="w-full bg-white text-indigo-700 hover:bg-indigo-50 font-semibold"
          data-testid="hero-export-board-pack-btn"
        >
          {dlBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5"/> : <FileDown className="h-3.5 w-3.5 mr-1.5"/>}
          Export Board Pack
        </Button>
      </Card>
    </div>
  );
}
